import React, { useState, useCallback, useRef, useEffect, useMemo, useContext } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Radio,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudIcon from '@mui/icons-material/Cloud';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseButton from 'components/@home/drawers/CloseButton';
import GoogleDriveIcon from 'assets/icons/googledrive.svg';
import OneDriveIcon from 'assets/icons/onedrive.svg';
import { bool, func, number, object } from 'prop-types';
import { useDispatch } from 'react-redux';
import Http from 'services/Http';
import { I18n } from 'utils/i18n';
import openCloudOAuth from 'utils/cloudOAuth';
import {
  useCreateCloudMirrorMutation,
  useLazyGetSharePointUrlQuery,
  useListCloudAccountsQuery,
  useListCloudMirrorsQuery,
  useDeleteCloudAccountMutation,
  useReconnectCloudMirrorMutation,
} from 'store/app/entities/CloudMirrorSlice';
import useOneDriveFilePicker from 'components/@home/@settings/Tabs/integrations/useOneDriveFilePicker';
import { ShareDrawerContext } from 'components/@home/@documents/SharedDocuments/ShareDrawer';
import { invalidateSharedFilesTags } from 'store/app/entities/SharedFilesSlice';

// Inject CSS so the Google Picker iframe renders above the Drawer (z-index 1200).
if (typeof document !== 'undefined' && !document.getElementById('cloud-picker-zfix')) {
  const s = document.createElement('style');
  s.id = 'cloud-picker-zfix';
  s.textContent =
    '.picker-dialog-bg { z-index: 1399 !important; } .picker-dialog { z-index: 1400 !important; }';
  document.head.appendChild(s);
}

const PROVIDERS = [
  {
    id: 'google',
    Icon: GoogleDriveIcon,
    label: 'Google Drive',
    blurbKey: 'ConnectedDrives.ProviderBlurb.google',
    apiProvider: 'google',
    driveType: 'google_drive',
  },
  {
    id: 'microsoft',
    Icon: OneDriveIcon,
    label: 'OneDrive / SharePoint',
    blurbKey: 'ConnectedDrives.ProviderBlurb.microsoft',
    apiProvider: 'microsoft',
    driveType: 'onedrive',
  },
];

// ── ProviderConnectRow ───────────────────────────────────────────────────────
// Empty-state source row: logo tile + name + what it covers + chevron. The whole
// row is the action; a per-provider spinner replaces the chevron while connecting.
const ProviderConnectRow = ({ provider, busy, disabled, onConnect }) => {
  const { Icon, label, blurbKey } = provider;
  return (
    <ButtonBase
      onClick={() => onConnect(provider.id)}
      disabled={disabled}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        width: '100%',
        p: '14px 16px',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        textAlign: 'left',
        justifyContent: 'flex-start',
        transition: 'background .15s, border-color .15s',
        opacity: disabled && !busy ? 0.5 : 1,
        '&:hover': disabled ? undefined : { borderColor: 'primary.main', bgcolor: 'action.hover' },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1.5,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box component={Icon} sx={{ width: 22, height: 22, color: 'background.default' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
          }}
        >
          {I18n.t(blurbKey)}
        </Typography>
      </Box>
      {busy ? (
        <CircularProgress size={18} sx={{ flexShrink: 0 }} />
      ) : (
        <ChevronRightIcon sx={{ flexShrink: 0, color: 'text.secondary' }} />
      )}
    </ButtonBase>
  );
};

ProviderConnectRow.propTypes = {
  provider: object.isRequired,
  busy: bool.isRequired,
  disabled: bool.isRequired,
  onConnect: func.isRequired,
};

// ── AccountKebab ────────────────────────────────────────────────────────────
const AccountKebab = ({ onReconnect, onDisconnect }) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton
        size="small"
        onClick={e => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{ flexShrink: 0 }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        <MenuItem
          onClick={() => {
            onReconnect();
            setAnchor(null);
          }}
        >
          <ListItemIcon>
            <SyncIcon fontSize="small" />
          </ListItemIcon>
          {I18n.t('ConnectedDrives.Reconnect')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDisconnect();
            setAnchor(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          {I18n.t('ConnectedDrives.Disconnect')}
        </MenuItem>
      </Menu>
    </>
  );
};

AccountKebab.propTypes = {
  onReconnect: func.isRequired,
  onDisconnect: func.isRequired,
};

// An account "needs reconnect" only in the Google legacy-scope case: a token
// minted with `drive.file` instead of `drive.readonly` can't browse folders.
// Microsoft accounts carry no such metadata, so they're always considered live.
const accountNeedsReconnect = acc =>
  acc._provider === 'google' && acc.metadata?.driveScope !== 'drive.readonly';

// ── ProviderAvatar ──────────────────────────────────────────────────────────
// Account initial in a circle with the provider glyph overlaid bottom-right,
// matching the badge treatment used on mirror rows in the folders list
// (see renderFolderCell.jsx) — no background circle behind the glyph.
const ProviderAvatar = ({ acc, size = 40 }) => {
  const ProviderIcon = PROVIDERS.find(p => p.id === acc._provider)?.Icon;
  const badge = Math.round(size * 0.4);
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: 'primary.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'primary.contrastText',
          fontWeight: 700,
          fontSize: size * 0.4,
        }}
      >
        {(acc.userInfo?.email || acc._id).slice(0, 1).toUpperCase()}
      </Box>
      {ProviderIcon && (
        <Box
          component={ProviderIcon}
          sx={{
            position: 'absolute',
            right: '-2px',
            bottom: '-2px',
            width: badge,
            height: badge,
            color: 'background.paper',
          }}
        />
      )}
    </Box>
  );
};

ProviderAvatar.propTypes = { acc: object.isRequired, size: number };

// ── AccountCard ─────────────────────────────────────────────────────────────
// A single-select radio row. The whole row is the hit target. Errored accounts
// can't be selected and surface a contextual "Reconnect" instead.
const AccountCard = ({ acc, mirrorCount, selected, onSelect, onReconnect, onDisconnect }) => {
  const isError = accountNeedsReconnect(acc);
  const selectable = !isError;
  const active = selected && selectable;
  const providerLabel = PROVIDERS.find(p => p.id === acc._provider)?.label || '';
  return (
    <Box
      onClick={selectable ? onSelect : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: '14px 16px',
        border: '1px solid',
        borderColor: active ? 'primary.main' : 'divider',
        borderRadius: 2,
        bgcolor: active ? 'primary.50' : 'background.paper',
        boxShadow: active ? theme => `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
        cursor: selectable ? 'pointer' : 'default',
        transition: 'background .15s, border-color .15s',
        '&:hover': selectable && !active ? { bgcolor: 'action.hover' } : undefined,
      }}
    >
      <Radio
        size="small"
        checked={!!active}
        disabled={!selectable}
        tabIndex={-1}
        sx={{ p: 0, flexShrink: 0 }}
      />
      <ProviderAvatar acc={acc} />
      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {acc.userInfo?.email || acc._id}
          </Typography>
          <Chip
            size="small"
            variant="soft"
            color={isError ? 'error' : 'success'}
            icon={
              isError ? (
                <ErrorOutlineIcon sx={{ fontSize: '14px !important' }} />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />
              )
            }
            label={
              isError
                ? I18n.t('ConnectedDrives.AccountStatus.reconnectNeeded')
                : I18n.t('ConnectedDrives.AccountStatus.connected')
            }
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mt: 0.25,
          }}
        >
          {providerLabel} ·{' '}
          {I18n.t(`ConnectedDrives.ConnectedSince${mirrorCount === 1 ? '' : '_plural'}`, {
            date: acc.createdAt
              ? new Date(acc.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
              : '—',
            count: mirrorCount,
          })}
        </Typography>
      </Box>
      {/* Errored accounts get a contextual Reconnect instead of the primary slot */}
      {isError && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<SyncIcon />}
          onClick={e => {
            e.stopPropagation();
            onReconnect();
          }}
          sx={{ flexShrink: 0 }}
        >
          {I18n.t('ConnectedDrives.Reconnect')}
        </Button>
      )}
      {/* Kebab */}
      <AccountKebab onReconnect={onReconnect} onDisconnect={onDisconnect} />
    </Box>
  );
};

AccountCard.propTypes = {
  acc: object.isRequired,
  mirrorCount: number.isRequired,
  selected: bool.isRequired,
  onSelect: func.isRequired,
  onReconnect: func.isRequired,
  onDisconnect: func.isRequired,
};

// ── DisconnectSheet ──────────────────────────────────────────────────────────
const DisconnectSheet = ({ account = null, open, onCancel, onConfirm }) => {
  const [mode, setMode] = useState('keep');
  const providerLabel = PROVIDERS.find(p => p.id === account?._provider)?.label || '';
  const count = account?.mirrorCount || 0;
  const has = count > 0;
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              bgcolor: 'error.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <WarningAmberIcon sx={{ color: 'error.main', fontSize: 20 }} />
          </Box>
          <Box>
            {I18n.t('ConnectedDrives.DisconnectTitle', {
              email: account?.userInfo?.email || account?._id,
            })}
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.25,
                fontWeight: 400,
              }}
            >
              {I18n.t('ConnectedDrives.DisconnectBody', { provider: providerLabel })}
              {has &&
                ` ${I18n.t(`ConnectedDrives.DisconnectBodyFolders${count === 1 ? '' : '_plural'}`, {
                  count,
                })}`}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      {has && (
        <DialogContent sx={{ pt: '4px !important' }}>
          {[
            {
              id: 'keep',
              title: I18n.t('ConnectedDrives.KeepFolders'),
              desc: I18n.t('ConnectedDrives.KeepFoldersDesc'),
              danger: false,
            },
            {
              id: 'remove',
              title: I18n.t('ConnectedDrives.RemoveFolders'),
              desc: I18n.t(`ConnectedDrives.RemoveFoldersDesc${count === 1 ? '' : '_plural'}`, {
                count,
              }),
              danger: true,
            },
          ].map(opt => {
            const selected = mode === opt.id;
            const accent = opt.danger ? 'error' : 'primary';
            return (
              <Box
                key={opt.id}
                onClick={() => setMode(opt.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  p: '12px 14px',
                  mb: 1,
                  border: '1px solid',
                  borderRadius: 2,
                  borderColor: selected ? `${accent}.main` : 'divider',
                  bgcolor: selected ? `${accent}.50` : 'background.paper',
                  cursor: 'pointer',
                }}
              >
                <Radio size="small" checked={selected} color={accent} sx={{ p: 0, mt: 0.25 }} />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: opt.danger && selected ? 'error.main' : 'text.primary',
                    }}
                  >
                    {opt.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {opt.desc}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onCancel}>{I18n.t('ConnectedDrives.Cancel')}</Button>
        <Button variant="contained" color="error" onClick={() => onConfirm(mode)}>
          {I18n.t('ConnectedDrives.Disconnect')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DisconnectSheet.propTypes = {
  account: object,
  open: bool.isRequired,
  onCancel: func.isRequired,
  onConfirm: func.isRequired,
};

// ── MirrorFolderDrawer (main) ───────────────────────────────────────────────
const MirrorFolderDrawer = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const shareDrawer = useContext(ShareDrawerContext);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const pickingForRef = useRef(null);
  const handleFolderPickedRef = useRef(null);
  const pendingMirrorRef = useRef(null);

  // ── state ──
  const [connectMenuAnchor, setConnectMenuAnchor] = useState(null);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [pickingFor, setPickingFor] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [pickError, setPickError] = useState(null);

  pickingForRef.current = pickingFor;

  // ── OneDrive native picker ──
  const [getSharePointUrl] = useLazyGetSharePointUrlQuery();
  const pickSharePointUrlRef = useRef(null);

  const {
    open: openOneDrivePicker,
    error: oneDriveError,
    PickerPortal,
  } = useOneDriveFilePicker({
    mode: 'folders',
    tokenId: () => pickingForRef.current?._id,
    getSharePointUrl: async () => pickSharePointUrlRef.current,
    onPick: items => {
      const item = items[0];
      if (!item) return;
      handleFolderPickedRef.current?.(pickingForRef.current, {
        externalId: item.id,
        externalDriveId: item.parentReference?.driveId || null,
        name: item.name,
        path: item.name,
        driveType: 'onedrive',
      });
    },
  });

  // ── queries / mutations ──
  const { data: googleData, refetch: refetchGoogle } = useListCloudAccountsQuery('google', {
    skip: !open,
  });
  const { data: msData, refetch: refetchMs } = useListCloudAccountsQuery('microsoft', {
    skip: !open,
  });
  const [deleteAccount] = useDeleteCloudAccountMutation();
  const [createMirror] = useCreateCloudMirrorMutation();
  const [reconnectMirror] = useReconnectCloudMirrorMutation();

  const { data: mirrorsData } = useListCloudMirrorsQuery(undefined, { skip: !open });

  const allAccounts = [
    ...(googleData?.accounts || []).map(a => ({ ...a, _provider: 'google' })),
    ...(msData?.accounts || []).map(a => ({ ...a, _provider: 'microsoft' })),
  ];

  // How many mirrored folders each account owns — drives the disconnect dialog's
  // keep-snapshot vs remove choice. Match by email, falling back to tokenId.
  const mappings = useMemo(() => mirrorsData?.mappings || [], [mirrorsData]);
  const countMirrorsForAccount = useCallback(
    acc =>
      mappings.filter(
        m =>
          m.tokenId === acc._id ||
          (m.accountEmail &&
            m.accountEmail === acc.userInfo?.email &&
            m.provider === acc._provider),
      ).length,
    [mappings],
  );

  // reset on close
  useEffect(() => {
    if (!open) {
      setConnectMenuAnchor(null);
      setConnectingProvider(null);
      setSelectedId(null);
      setPickingFor(null);
      setPickError(null);
      setDisconnecting(null);
    }
  }, [open]);

  useEffect(() => {
    if (!shareDrawer.isOpen) {
      pendingMirrorRef.current = null;
    }
  }, [shareDrawer.isOpen]);

  // ── auth ──
  const refetchRef = useRef({ google: refetchGoogle, microsoft: refetchMs });
  refetchRef.current = { google: refetchGoogle, microsoft: refetchMs };

  const handleAuth = useCallback(
    async providerId => {
      setConnectMenuAnchor(null);
      setConnectingProvider(providerId);
      try {
        const tokenId = await openCloudOAuth(providerId);
        refetchRef.current[providerId]?.();
        // Re-link any folders this account previously owned that need re-auth.
        // For a brand-new account this matches nothing and is a harmless no-op.
        await reconnectMirror({ tokenId });
      } catch {
        // popup blocked / cancelled / provider error — nothing to recover
      } finally {
        setConnectingProvider(null);
      }
    },
    [reconnectMirror],
  );

  const handleMirrorPermissionsSubmit = useCallback(
    async ({ permissions } = {}) => {
      if (!permissions) {
        return;
      }
      const pending = pendingMirrorRef.current;
      if (!pending) {
        return;
      }
      try {
        await createMirror({
          tokenId: pending.account._id,
          provider: pending.providerCfg.apiProvider,
          driveType: pending.folder.driveType || pending.providerCfg.driveType,
          externalDriveId: pending.folder.externalDriveId || null,
          externalFolderId: pending.folder.externalId,
          externalPath: pending.folder.path,
          name: pending.folder.name,
          permissions,
        });
        dispatch(invalidateSharedFilesTags([{ type: 'SharedFiles' }]));
        pendingMirrorRef.current = null;
        onCloseRef.current();
      } catch {
        // error — user can retry via Choose folder
      }
    },
    [createMirror, dispatch],
  );

  // ── Create mirror (after folder picked + team permissions assigned) ──
  const handleFolderPicked = useCallback(
    async (account, folder) => {
      setPickingFor(null);
      const providerCfg = PROVIDERS.find(p => p.id === account._provider);
      if (!providerCfg) return;
      pendingMirrorRef.current = { account, folder, providerCfg };
      shareDrawer.open(
        { kind: 'Folder', permissions: [], name: folder.name },
        handleMirrorPermissionsSubmit,
      );
    },
    [handleMirrorPermissionsSubmit, shareDrawer],
  );

  handleFolderPickedRef.current = handleFolderPicked;

  // ── Google Picker ──
  const openPickerWithToken = useCallback(accessToken => {
    const builder = new window.google.picker.PickerBuilder()
      .addView(
        new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
          .setSelectFolderEnabled(true)
          .setIncludeFolders(true),
      )
      .setOAuthToken(accessToken)
      .setOrigin(window.location.origin)
      .setCallback(pickerData => {
        if (pickerData.action === window.google.picker.Action.PICKED) {
          const f = pickerData.docs[0];
          handleFolderPickedRef.current?.(pickingForRef.current, {
            externalId: f.id,
            name: f.name,
            externalDriveId: null,
            path: f.name,
          });
        }
      });
    builder.build().setVisible(true);
  }, []);

  const openGooglePicker = useCallback(
    async tokenId => {
      try {
        const { data } = await Http.post('/v1/google-oauth/picker-token', { tokenId });
        if (!window.gapi) {
          const script = document.createElement('script');
          script.src = 'https://apis.google.com/js/api.js';
          script.onload = () =>
            window.gapi.load('picker', () => openPickerWithToken(data.accessToken));
          document.head.appendChild(script);
        } else {
          window.gapi.load('picker', () => openPickerWithToken(data.accessToken));
        }
      } catch {
        // ignore
      }
    },
    [openPickerWithToken],
  );

  // ── Choose folder ──
  const handleChooseFolder = useCallback(
    async acc => {
      setPickError(null);
      setPickingFor(acc);
      if (acc._provider === 'microsoft') {
        let url = acc.metadata?.sharePointUrl || null;
        if (!url) {
          try {
            const res = await getSharePointUrl(acc._id).unwrap();
            url = res?.sharePointUrl || null;
          } catch {
            setPickError(I18n.t('ConnectedDrives.SharePointUrlError'));
            setPickingFor(null);
            return;
          }
        }
        if (!url) {
          setPickError(I18n.t('ChatFlowSettings.Personal account not supported'));
          setPickingFor(null);
          return;
        }
        pickSharePointUrlRef.current = url;
        openOneDrivePicker();
      } else {
        openGooglePicker(acc._id);
      }
    },
    [getSharePointUrl, openGooglePicker, openOneDrivePicker],
  );

  // ── Disconnect ──
  const handleDisconnect = useCallback(
    async mode => {
      if (!disconnecting) return;
      await deleteAccount({ id: disconnecting._id, mode });
      setDisconnecting(null);
    },
    [disconnecting, deleteAccount],
  );

  const hasAccounts = allAccounts.length > 0;
  // Only non-errored accounts are selectable; an errored selection resolves to null.
  const selectedAccount =
    allAccounts.find(a => a._id === selectedId && !accountNeedsReconnect(a)) || null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 680 } } } }}
    >
      <Box sx={{ display: 'flex', height: '100%', position: 'relative' }}>
        {/* Close button column */}
        <Box sx={{ paddingTop: 2.5, paddingLeft: 2.5, flexShrink: 0 }}>
          <CloseButton onClick={onClose} />
        </Box>

        {/* Main column */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              mt: 2.5,
              ml: 2,
              mr: 2.5,
              mb: 2,
            }}
          >
            <Typography variant="h5">{I18n.t('ConnectedDrives.AddFolder')}</Typography>
            {hasAccounts && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                {I18n.t('ConnectedDrives.DrawerSubtitle')}
              </Typography>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'scroll', overflowX: 'hidden', px: 2 }}>
            {!hasAccounts ? (
              // Empty state — anchored in the upper third, not dead-centered
              <Box sx={{ maxWidth: 440, mx: 'auto', pt: '14vh', pb: 5 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    // Translucent indigo tint reads correctly in both themes,
                    // unlike a fixed light token.
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.14),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.25,
                  }}
                >
                  <CloudIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {I18n.t('ConnectedDrives.EmptyStateMultiTitle')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mt: 1,
                    mb: 2.75,
                    lineHeight: 1.5,
                  }}
                >
                  {I18n.t('ConnectedDrives.EmptyStateMultiBody')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {PROVIDERS.map(provider => (
                    <ProviderConnectRow
                      key={provider.id}
                      provider={provider}
                      busy={connectingProvider === provider.id}
                      disabled={!!connectingProvider}
                      onConnect={handleAuth}
                    />
                  ))}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2.25,
                    color: 'text.secondary',
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                  <Typography variant="caption">
                    {I18n.t('ConnectedDrives.ReadOnlyFootnote')}
                  </Typography>
                </Box>
              </Box>
            ) : (
              // Account list
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1.5,
                    minHeight: 32,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                    }}
                  >
                    {I18n.t('ConnectedDrives.ConnectedAccounts')} · {allAccounts.length}
                  </Typography>
                  {/* Connect account dropdown */}
                  {connectingProvider ? (
                    <CircularProgress size={20} />
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<AddIcon />}
                        endIcon={<ArrowDropDownIcon />}
                        onClick={e => setConnectMenuAnchor(e.currentTarget)}
                        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                      >
                        {I18n.t('ConnectedDrives.ConnectAccount')}
                      </Button>
                      <Menu
                        anchorEl={connectMenuAnchor}
                        open={!!connectMenuAnchor}
                        onClose={() => setConnectMenuAnchor(null)}
                      >
                        {PROVIDERS.map(({ id, Icon, label }) => (
                          <MenuItem key={id} onClick={() => handleAuth(id)}>
                            <ListItemIcon>
                              <Box
                                component={Icon}
                                sx={{ width: 18, height: 18, color: 'background.paper' }}
                              />
                            </ListItemIcon>
                            {label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {allAccounts.map(acc => (
                    <AccountCard
                      key={acc._id}
                      acc={acc}
                      mirrorCount={countMirrorsForAccount(acc)}
                      selected={selectedId === acc._id}
                      onSelect={() => setSelectedId(acc._id)}
                      onReconnect={() => handleAuth(acc._provider)}
                      onDisconnect={() =>
                        setDisconnecting({ ...acc, mirrorCount: countMirrorsForAccount(acc) })
                      }
                    />
                  ))}
                </Box>
                {(pickError || oneDriveError) && (
                  <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
                    {pickError || oneDriveError}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Footer — single primary CTA, enabled once an account is selected */}
          {hasAccounts && (
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2.5,
                py: 1.75,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedAccount
                  ? I18n.t('ConnectedDrives.PickFolderFrom', {
                      email: selectedAccount.userInfo?.email || selectedAccount._id,
                    })
                  : I18n.t('ConnectedDrives.SelectAccountPrompt')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<FolderOpenIcon />}
                disabled={!selectedAccount}
                onClick={() => selectedAccount && handleChooseFolder(selectedAccount)}
                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {I18n.t('ConnectedDrives.PickFolder')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* OneDrive native picker portal */}
      <PickerPortal />

      {/* Disconnect dialog */}
      <DisconnectSheet
        open={!!disconnecting}
        account={disconnecting}
        onCancel={() => setDisconnecting(null)}
        onConfirm={handleDisconnect}
      />
    </Drawer>
  );
};

MirrorFolderDrawer.propTypes = { open: bool.isRequired, onClose: func.isRequired };

export default MirrorFolderDrawer;
